import type { paths } from "@/api-schema";
import { handlePaginatedParameters } from "@/common/api/_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "@/common/api/_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "@/common/api/_types";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";

export const CASAVI_CONTACT_BLACKLIST_SEARCH_RESOURCE = "casavi-contact-blacklist" as const;
export const CASAVI_CONTACT_BLACKLIST_SEARCH_OPERATION = "search" as const;
export const CASAVI_CONTACT_BLACKLIST_SEARCH_API_PATH =
  `/v1/${CASAVI_CONTACT_BLACKLIST_SEARCH_RESOURCE}/${CASAVI_CONTACT_BLACKLIST_SEARCH_OPERATION}` as const;

export type CasaviContactBlacklistSearchParameters =
  paths[typeof CASAVI_CONTACT_BLACKLIST_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type CasaviContactBlacklistSearchBody =
  paths[typeof CASAVI_CONTACT_BLACKLIST_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type CasaviContactBlacklistSearchResponse =
  paths[typeof CASAVI_CONTACT_BLACKLIST_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useCasaviContactBlacklistSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<CasaviContactBlacklistSearchParameters, CasaviContactBlacklistSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    CasaviContactBlacklistSearchResponse,
    Error,
    CasaviContactBlacklistSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<
      PaginatedOptions<CasaviContactBlacklistSearchParameters, CasaviContactBlacklistSearchBody>
    >(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      CASAVI_CONTACT_BLACKLIST_SEARCH_RESOURCE,
      CASAVI_CONTACT_BLACKLIST_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<CasaviContactBlacklistSearchResponse>(
        CASAVI_CONTACT_BLACKLIST_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );
      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
